import { usePaths, useRouter } from "@engaging-tech/routing"
import React from "react"
import { useDispatch, useSelector } from "react-redux"

import { resetCode } from "../../store/survey.actions"
import {
  getCodeResponse,
  getIsAnonymous,
  getReportId,
  getRequiresCode,
  getSurveyType
} from "../../store/survey.selectors"
import CodeAlreadyUsed from "./codeAlreadyUsed"
import CodeCheckContent from "./codeCheckContent"
import InvalidCode from "./invalidCode"
import SendResultsViaEmailAgain from "./sendResultsViaEmailAgain"

// Helper: Determines if code screen and API validation are required
export const getCodeRequirements = ({ uniqueSubmission, isAnonymous }) => {
  // NS&E or other types
  if (isAnonymous) {
    return {
      requireCodeScreen: uniqueSubmission === true,
      requireApiValidation: false
    }
  }

  // Not anonymous
  return {
    requireCodeScreen: uniqueSubmission === true,
    requireApiValidation: true
  }
}

// Helper: Determines post-code API response flow
export const getSurveyFlow = (isCodeUsed, reportId, paths) => {
  switch (isCodeUsed) {
    case 1:
      return { action: "startSurvey" }
    case 2:
      return { action: "codeAlreadyUsed" }
    case 3:
      return { action: "invalidCode" }
    case 4:
      return { action: "redirectResults", redirectPath: paths.survey.results.replace(":id", reportId) }
    case 5:
      return { action: "emailResults" }
    default:
      return { action: null }
  }
}

const CodeCheck = ({ onNext, onPrevious }) => {
  const router = useRouter()
  const paths = usePaths()
  const dispatch = useDispatch()
  const uniqueSubmission = useSelector(getRequiresCode)
  const isAnonymous = useSelector(getIsAnonymous)
  const codeResponse = useSelector(getCodeResponse)
  const reportId = useSelector(getReportId)
  const surveyType = useSelector(getSurveyType)
  const { requireCodeScreen, requireApiValidation } = getCodeRequirements({
    uniqueSubmission,
    isAnonymous
  })

  const flow = getSurveyFlow(codeResponse, reportId, paths)

  const handlePrevious = () => {
    dispatch(resetCode())
    onPrevious()
  }

  if (flow.action === "startSurvey") onNext()
  if (flow.action === "redirectResults") router.navigate(flow.redirectPath)
  if (flow.action === "codeAlreadyUsed" && !isAnonymous)
    return <CodeAlreadyUsed onNext={onNext} onPrevious={handlePrevious} />
  if (flow.action === "invalidCode") return <InvalidCode onNext={onNext} onPrevious={handlePrevious} />
  if (flow.action === "emailResults") return <SendResultsViaEmailAgain />
  return (
    requireCodeScreen && (
      <CodeCheckContent
        onNext={onNext}
        onPrevious={handlePrevious}
        requireApiValidation={requireApiValidation}
        flow={flow}
        surveyType={surveyType}
      />
    )
  )
}

export default CodeCheck
